import { XIcon } from "lucide-react";
import { motion } from "motion/react";
import type * as React from "react";
import {
  Children,
  createContext,
  isValidElement,
  use,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";

import { Badge } from "@/web/components/primitives/badge";
import { Button, type ButtonProps } from "@/web/components/primitives/button";
import {
  Swipable,
  type SwipableBehaviorProps,
  type SwipeDirection,
} from "@/web/components/primitives/swipable";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/web/components/primitives/tooltip";
import { motionTransitions } from "@/web/lib/motion-tokens";
import { cn } from "@/web/lib/styling";
import {
  CATEGORY_PALETTES,
  type CategoryPalette,
} from "@/web/routes/_protected/smb/-dashboard/-categoryPalettes";
import { useDeckCardCoordination } from "@/web/routes/_protected/smb/-dashboard/-DeckContext";
import type { SmbActionCategory } from "@/web/routes/_protected/smb/-dashboard/-types";

const SKIP_ONLY_DIRECTIONS: readonly SwipeDirection[] = ["left"];

type ActionCardContextValue = {
  category: SmbActionCategory;
  palette: CategoryPalette;
  flipToBack: () => void;
  flipToFront: () => void;
  registerTriggerRef: (el: HTMLButtonElement | null) => void;
  registerCloseRef: (el: HTMLButtonElement | null) => void;
  /**
   * Whether swipe-left-to-skip is actually draggable *right now*.
   *
   * Only the root can answer this: it's false when a card supplies its own
   * `swipe` (which replaces the skip gesture), and false while the gesture is
   * suppressed by `swipeDisabled` or a card flip. Subcomponents must not
   * re-derive it from the deck context — that misses both cases and lets the
   * Skip button advertise a gesture that isn't there.
   */
  isSkipSwipeLive: boolean;
};

const ActionCardContext = createContext<ActionCardContextValue | null>(null);

function useActionCardContext(): ActionCardContextValue {
  const ctx = use(ActionCardContext);
  if (ctx == null) {
    throw new Error("ActionCard subcomponents must be rendered inside <ActionCard />");
  }
  return ctx;
}

/** Read the current category from inside the card tree (e.g. from a body widget). */
export function useActionCardCategory(): SmbActionCategory {
  return useActionCardContext().category;
}

/**
 * Like {@link useActionCardCategory} but returns `null` instead of throwing when
 * there's no surrounding `<ActionCard />`. For widgets (e.g. `BudgetSlider`)
 * that can render both inside a card body and standalone in a drawer.
 */
export function useActionCardCategoryOptional(): SmbActionCategory | null {
  return use(ActionCardContext)?.category ?? null;
}

/** Read the current palette from inside the card tree. */
export function useActionCardPalette(): CategoryPalette {
  return useActionCardContext().palette;
}

function ActionCardRoot({
  category,
  className,
  children,
  swipe,
  swipeDisabled = false,
  hero = false,
  ...props
}: React.ComponentProps<"article"> & {
  category: SmbActionCategory;
  /**
   * Escape hatch for cards that need bespoke swipe behavior. When omitted (the
   * normal case) the card gets the deck's universal swipe-left-to-skip.
   */
  swipe?: SwipableBehaviorProps;
  /** Disables swipe gestures (e.g. while a portaled modal drawer is open). */
  swipeDisabled?: boolean;
  /**
   * Render the face transparent so a caller-provided `style.backgroundImage`
   * (or an absolutely-positioned `<HeroPlaceholder />`) shows through. The
   * outer `<article>` falls back to `bg-muted` so a missing hero degrades to
   * a gray card instead of a see-through hole over the deck stack.
   */
  hero?: boolean;
}) {
  const palette = CATEGORY_PALETTES[category];
  const deck = useDeckCardCoordination();
  let back: React.ReactNode = null;
  const front: React.ReactNode[] = [];
  Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === ActionCardBack) {
      back = child;
    } else {
      front.push(child);
    }
  });
  const [isFlipped, setIsFlipped] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  // Flip handlers `flushSync` the state update so the back/front face's `inert`
  // attribute is committed to the DOM before we move focus — without that, the
  // element we're about to focus is still inert and the `.focus()` no-ops.
  // `flushSync` commits `inert` / `aria-hidden` before focus moves — otherwise
  // `.focus()` can no-op on an element that is still inert.
  const flipToBack = useCallback(() => {
    flushSync(() => {
      setIsFlipped(true);
    });
    closeRef.current?.focus();
  }, []);
  const flipToFront = useCallback(() => {
    const shouldRestoreFocus = document.activeElement === closeRef.current;
    flushSync(() => setIsFlipped(false));
    if (shouldRestoreFocus) {
      triggerRef.current?.focus();
    }
  }, []);
  const registerTriggerRef = useCallback((el: HTMLButtonElement | null) => {
    triggerRef.current = el;
  }, []);
  const registerCloseRef = useCallback((el: HTMLButtonElement | null) => {
    closeRef.current = el;
  }, []);

  // Universal swipe-left-to-skip. Every card in the deck gets it — gated on the
  // same `canSkip` that gates the Skip button, so the gesture and the button
  // are always available together and the tour never teaches a rule that's
  // false on some cards. Right-drag is suppressed entirely (see `directions`),
  // so the card visibly refuses to move that way.
  //
  // The gesture is a *trigger*, not an animator: the deck already owns the skip
  // peel (`exit-skip` → `x: -118%`, rotate, fade), so `exitMode: "handoff"`
  // fires `skip` on release and snaps the Swipable's own `x` straight back to
  // origin. Without the handoff the two animations race, and Swipable's
  // underdamped reset spring inherits the exit tween's velocity and overshoots
  // right — a card left sitting off-centre at the back of the stack.
  //
  // A card supplying its own `swipe` replaces this entirely.
  const skipSwipe: SwipableBehaviorProps | null =
    swipe == null && deck?.skip != null && deck.viewMode === "stack"
      ? {
          directions: SKIP_ONLY_DIRECTIONS,
          exitMode: "handoff",
          onSwipeLeft: deck.skip,
        }
      : null;
  const activeSwipe = swipe ?? skipSwipe;
  const isSwipeSuppressed = isFlipped || swipeDisabled;
  // Single source of truth for "can the user swipe this card away right now",
  // shared by the Swipable itself and by the Skip button's tooltip so the hint
  // can never outlive the gesture it describes.
  const isSkipSwipeLive = skipSwipe != null && !isSwipeSuppressed;

  const ctx = useMemo(
    () => ({
      category,
      palette,
      flipToBack,
      flipToFront,
      registerTriggerRef,
      registerCloseRef,
      isSkipSwipeLive,
    }),
    [
      category,
      palette,
      flipToBack,
      flipToFront,
      registerTriggerRef,
      registerCloseRef,
      isSkipSwipeLive,
    ],
  );

  const faceClass = cn(
    "flex aspect-7/10 w-full flex-col gap-6 p-6 [grid-area:1/1]",
    "overflow-hidden border border-withus-dark-blue backface-hidden",
    hero ? "bg-transparent" : palette.cardBg,
    hero ? "" : palette.onCardFg,
  );

  const card = (
    <article
      data-slot="smb-action-card"
      data-category={category}
      data-hero={hero}
      className={cn(
        "mx-auto w-full  perspective-distant sm:max-w-88",
        hero ? "bg-muted" : "",
        className,
      )}
      {...props}
    >
      <motion.div
        data-slot="smb-action-card-flipper"
        data-flipped={isFlipped}
        className="grid shadow-withus-default transform-3d"
        initial={false}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={motionTransitions.flip}
      >
        <div
          data-slot="smb-action-card-front"
          className={cn(faceClass, "relative")}
          aria-hidden={isFlipped}
          inert={isFlipped}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-6 contain-layout [overflow-anchor:none]">
            {front}
          </div>
        </div>
        <div
          data-slot="smb-action-card-back"
          className={cn(faceClass, "rotate-y-180")}
          aria-hidden={!isFlipped}
          inert={!isFlipped}
        >
          {back}
        </div>
      </motion.div>
    </article>
  );

  // Default `commitProgress` of 0.4: the card animates ~40% of the way out on
  // release, sits during any async work, then finishes the remaining ~60% on
  // resolve. Hides server latency behind continued motion. Consumers can
  // override via the `swipe.commitProgress` prop (it's spread after the default).
  return (
    <ActionCardContext value={ctx}>
      {activeSwipe == null ? (
        card
      ) : (
        <Swipable
          commitProgress={0.4}
          {...activeSwipe}
          disabled={activeSwipe.disabled || isSwipeSuppressed}
        >
          {card}
        </Swipable>
      )}
    </ActionCardContext>
  );
}

function ActionCardHeader({ className, ...props }: React.ComponentProps<"header">) {
  return (
    <header
      data-slot="smb-action-card-header"
      className={cn("flex w-full items-center justify-between gap-4", className)}
      {...props}
    />
  );
}

function ActionCardBadge({ className, children, ...props }: React.ComponentProps<"div">) {
  const { palette } = useActionCardContext();
  return (
    <Badge
      variant="ghost"
      size="sm"
      data-slot="smb-action-card-badge"
      className={cn("text-[0.6875rem] leading-none tracking-wider", palette.badgeClass, className)}
      {...props}
    >
      {children}
    </Badge>
  );
}

/**
 * Inline button that flips the card to the back face. Place inside
 * `<ActionCard.Header>` next to the Badge. Pair with a sibling
 * `<ActionCard.Back>` so there's something to flip to — without one, the
 * button still flips but the back face will be empty.
 */
function ActionCardDetailsTrigger({
  className,
  children = "Details",
  ...props
}: Omit<ButtonProps, "variant" | "size" | "onClick" | "ref">) {
  const { flipToBack, registerTriggerRef } = useActionCardContext();
  return (
    <Button
      ref={registerTriggerRef}
      variant="link"
      size="sm"
      onClick={flipToBack}
      data-slot="smb-action-card-details-trigger"
      className={className}
      {...props}
    >
      {children}
    </Button>
  );
}

/**
 * Back-face content slot. Declare as a top-level child of `<ActionCard>`
 * alongside Header/Main/Body/Footer; the root extracts it at render time and
 * renders it on the reverse side of the card. Partner with
 * `<ActionCard.DetailsTrigger>` in the Header so users can actually reach it.
 */
function ActionCardBack({
  title,
  description,
  children,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { flipToFront, registerCloseRef } = useActionCardContext();
  return (
    <>
      <header className="flex w-full items-start justify-between gap-4">
        <h3 className="withus-strong text-xl">{title}</h3>
        <Button
          ref={registerCloseRef}
          variant="ghost"
          size="icon"
          onClick={flipToFront}
          aria-label="Close details"
          data-slot="smb-action-card-back-close"
        >
          <XIcon aria-hidden />
        </Button>
      </header>
      {description ? <p className="text-sm leading-snug opacity-70">{description}</p> : null}
      <div className="flex-1 text-sm leading-relaxed">{children}</div>
    </>
  );
}

function ActionCardTitle({ className, children, ...props }: React.ComponentProps<"h3">) {
  return (
    <h3
      data-slot="smb-action-card-title"
      className={cn("withus-strong text-xl", className)}
      {...props}
    >
      {children}
    </h3>
  );
}

function ActionCardDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="smb-action-card-description"
      className={cn("text-sm leading-snug opacity-70", className)}
      {...props}
    />
  );
}

/**
 * Optional middle region between `Main` and `Footer`. It grows to fill any
 * remaining vertical space and scrolls internally when content exceeds card
 * height, keeping all cards at identical height.
 */
function ActionCardBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="smb-action-card-body"
      className={cn("min-h-0 w-full flex-1 overflow-y-auto", className)}
      {...props}
    />
  );
}

/** Groups the title, description and body in a single column at the top. */
function ActionCardMain({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="smb-action-card-main"
      className={cn("flex w-full flex-col gap-3", className)}
      {...props}
    />
  );
}

function ActionCardFooter({ className, ...props }: React.ComponentProps<"footer">) {
  return (
    <footer
      data-slot="smb-action-card-footer"
      className={cn("mt-auto flex w-full items-center gap-2", className)}
      {...props}
    />
  );
}

function ActionCardSkipButton({
  onSkip,
  className,
  children = "Skip",
  color,
  ...props
}: Omit<ButtonProps, "onClick" | "disabled" | "variant"> & {
  onSkip?: () => void;
}) {
  const { palette, isSkipSwipeLive } = useActionCardContext();
  const button = (
    <Button
      variant="outline"
      color={color ?? palette.skipButtonColor}
      size="md"
      data-slot="smb-action-card-skip"
      onClick={onSkip}
      disabled={onSkip == null}
      className={cn(palette.skipButtonClass, className)}
      {...props}
    >
      {children}
    </Button>
  );

  // Advertise the gesture from the button that does the same thing — but only
  // while the gesture is actually live, so the hint is never a lie. That check
  // is `isSkipSwipeLive` from the root, not a re-derivation from the deck
  // context: the root is the only place that knows the card didn't override
  // `swipe`, and that nothing (an open note drawer, a card flip) is currently
  // suppressing the drag. Hover-only by design (no `useTouch`): tapping the
  // trigger on a touch device would skip the card, and the tour carries
  // discovery there instead.
  if (onSkip == null || !isSkipSwipeLive) return button;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>Or swipe the card left</TooltipContent>
    </Tooltip>
  );
}

function ActionCardPrimaryButton({
  ref,
  className,
  children,
  color,
  ...props
}: Omit<ButtonProps, "variant"> & { ref?: React.Ref<HTMLButtonElement> }) {
  const { palette } = useActionCardContext();
  return (
    <Button
      ref={ref}
      variant="solid"
      color={color ?? palette.primaryButtonColor}
      size="md"
      data-slot="smb-action-card-primary"
      className={cn("w-full", palette.primaryButtonClass, className)}
      {...props}
    >
      {children}
    </Button>
  );
}

export const ActionCard = Object.assign(ActionCardRoot, {
  Header: ActionCardHeader,
  Badge: ActionCardBadge,
  DetailsTrigger: ActionCardDetailsTrigger,
  Back: ActionCardBack,

  Main: ActionCardMain,
  Title: ActionCardTitle,
  Description: ActionCardDescription,
  Body: ActionCardBody,
  Footer: ActionCardFooter,
  SkipButton: ActionCardSkipButton,
  PrimaryButton: ActionCardPrimaryButton,
});
